import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    Component,
    OnInit,
    Optional,
    PLATFORM_ID,
    inject,
    OnDestroy,
    Inject,
    Renderer2,
    ElementRef
} from '@angular/core';
import { Router, NavigationEnd, NavigationCancel, NavigationError, ResolveEnd } from '@angular/router';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { TranslateService } from '@ngx-translate/core';
import { IxfiLanguageService, IxfiThemeService } from 'header';
import { map, fromEvent, merge, Observable, of, Subscription, filter, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TermsAndConditionsService } from './core/services/terms-and-conditions.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    isLoading = true;
    isBrowser!: boolean;
    online$!: Observable<boolean>;

    subscriptions: Subscription[] = [];

    private router = inject(Router);
    private document = inject(DOCUMENT);
    private renderer = inject(Renderer2);
    private platformId = inject(PLATFORM_ID);
    private tncService = inject(TermsAndConditionsService);
    private themeService = inject(IxfiThemeService);
    private translateService = inject(TranslateService);
    private languageService = inject(IxfiLanguageService);
    private elementRef = inject(ElementRef);

    constructor(@Optional() @Inject(REQUEST) private httpRequest) {
        this.loaderSubscription();
    }

    ngOnInit(): void {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.hideAngularVersion();
        this.tncService.openTermsAndConditions();
        if (!this.isBrowser) {
            if (this.httpRequest && this.httpRequest.cookies) {
                // SET THEME for SERVER
                const isDarkMode = this.httpRequest.cookies.isDarkMode === 'true' || false;
                this.themeService.toggleTheme(isDarkMode);

                // SET LANGUAGE for SERVER
                if (this.httpRequest.cookies.selectedLanguage) {
                    const lang = JSON.parse(this.httpRequest.cookies.selectedLanguage) || null;
                    if (lang) this.languageService.updateLanguage(lang);
                }
            }
        }

        /* Add preconnect to <head> </head> */
        this.addPreconnectToHead();

        /**
         * Set the default language to english by loading the en.json file
         */
        this.translateService.setDefaultLang('en');

        /**
         * Check If the system is connected to the internet
         * Merge 3 streams and run async pipe so there's concurrency with the result
         */
        this.online$ = merge(
            of(navigator.onLine),
            fromEvent(window, 'online').pipe(map(_ => true)),
            fromEvent(window, 'offline').pipe(map(_ => false))
        );
    }

    private addPreconnectToHead(): void {
        /**
         * Add a <link rel="preconnect" href="https://my.cdn.origin" /> resource link in the <head> of the document
         * This ensures the LCP image loads as quickly as possible.
         */
        const linkElement: HTMLLinkElement = this.renderer.createElement('link');
        linkElement.rel = 'preconnect';
        linkElement.href = environment.CDN_URL;

        this.renderer.listen(linkElement, 'load', () => {});

        this.renderer.appendChild(this.document.head, linkElement);
    }

    loaderSubscription() {
        const routerSub: Subscription = this.router.events
            .pipe(
                // Filter for navigation events only
                filter(
                    event =>
                        event instanceof NavigationEnd ||
                        event instanceof NavigationCancel ||
                        event instanceof NavigationError
                ),
                // Set loading state based on event type
                tap(event => {
                    if (event instanceof ResolveEnd) {
                        this.isLoading = true;
                    } else {
                        this.isLoading = false;
                    }
                })
            )
            .subscribe();

        this.subscriptions.push(routerSub);
    }

    hideAngularVersion() {
        this.elementRef?.nativeElement?.removeAttribute('ng-version');
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => {
            if (sub) sub.unsubscribe();
        });
    }
}
